import { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography, Collapse, Grid } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DataLakeDirectoryModel, DataLakeFileModel } from '../../gql-types.generated';
import { ExpandMore90 } from '../../util/SharedStyles';
import theme from '../../Theme';

const TreeItemContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
}));

interface StyledTreeItemContentProps {
    active?: boolean;
    disabled?: boolean;
}

const TreeItemContent = styled(Grid, {
    shouldForwardProp: (prop) => prop !== 'active' && prop !== 'disabled'
})<StyledTreeItemContentProps>(({ theme, active, disabled }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '4px 8px',
    cursor: disabled ? 'default' : 'pointer',
    minWidth: 0, // Allows child elements to shrink below their content size
    backgroundColor: active ? theme.palette.action.selected : 'transparent',
    '&:hover': disabled ? {} : {
        backgroundColor: active 
            ? theme.palette.action.selected 
            : theme.palette.action.hover,
    },
}));

const TreeItemLabel = styled(Typography)({
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

const FileItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '4px 8px',
    marginLeft: '40px',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

interface DirectoryTreeItemProps {
    directory: DataLakeDirectoryModel;
    level: number;
    onDirectoryClick: (path: string) => void;
    expanded: boolean;
    onToggleExpand: (path: string) => void;
    expandedPaths: Set<string>;
    activeDirectory?: DataLakeDirectoryModel;
    disabledPaths?: string[];
}

const DirectoryTreeItem: React.FC<DirectoryTreeItemProps> = (props) => {
    const { directory, level, expanded, expandedPaths, onDirectoryClick, onToggleExpand, activeDirectory, disabledPaths } = props;
    const isActive = activeDirectory?.path === directory.path;
    const isDisabled = disabledPaths?.includes(directory.path);
    
    const handleClick = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
        if (!isDisabled) {
            onDirectoryClick(directory.path);
        }
    }, [directory.path, onDirectoryClick]);

    const handleExpandClick = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
        onToggleExpand(directory.path);
    }, [directory.path, onToggleExpand]);

    const hasChildren = (directory.childDirectories && directory.childDirectories.length > 0) || 
                       (directory.dataLakeFileModels && directory.dataLakeFileModels.length > 0);

    return (
        <TreeItemContainer>
            <TreeItemContent 
                container
                style={{ paddingLeft: `${level * 20}px` }}
                onClick={handleClick}
                active={isActive}
                disabled={isDisabled}
            >
                <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                    <Box width="28px" display="flex" justifyContent="center">
                        {hasChildren && (
                            <ExpandMore90
                                expand={+expanded}
                                onClick={handleExpandClick}
                                aria-label="show more"
                                size="small"
                                sx={{ cursor: 'pointer' }}
                            >
                                <ChevronRightIcon />
                            </ExpandMore90>
                        )}
                    </Box>
                    <Box 
                        display="flex" 
                        alignItems="center" 
                        marginRight="4px" 
                        sx={{ 
                            opacity: isDisabled ? 0.5 : 1,
                        }}
                    >
                        {expanded ? <FolderOpenIcon sx={{ color: theme.palette.secondary.main }} /> : <FolderIcon sx={{ color: theme.palette.secondary.main }} />}
                    </Box>
                </Grid>
                <Grid 
                    item 
                    xs={true} 
                    style={{ minWidth: 0 }}
                    sx={{ 
                        opacity: isDisabled ? 0.5 : 1,
                    }}
                >
                    <TreeItemLabel variant="body1">{directory.name}</TreeItemLabel>
                </Grid>
            </TreeItemContent>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                {directory.dataLakeFileModels
                    ?.filter((file): file is DataLakeFileModel => !!file && !!file.name && !!file.path)
                    .map(file => (
                        <FileItem 
                            key={file.path}
                            style={{ marginLeft: `${(level + 1) * 20}px` }}
                        >
                            <InsertDriveFileIcon sx={{ color: theme.palette.grayscale.main, marginRight: '8px' }} />
                            <TreeItemLabel variant="body2">{file.name}</TreeItemLabel>
                        </FileItem>
                    ))}
                
                {directory.childDirectories
                    ?.filter((dir): dir is DataLakeDirectoryModel => !!dir && !!dir.name && !!dir.path)
                    .map(childDir => (
                    <DirectoryTreeItem
                        key={childDir.path}
                        directory={childDir}
                        level={level + 1}
                        onDirectoryClick={onDirectoryClick}
                        expanded={expandedPaths.has(childDir.path)}
                        onToggleExpand={onToggleExpand}
                        expandedPaths={expandedPaths}
                        activeDirectory={activeDirectory}
                        disabledPaths={disabledPaths}
                    />
                ))}
            </Collapse>
        </TreeItemContainer>
    );
};

export default DirectoryTreeItem;
